import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, CssBaseline } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useRecordingNavigationGuard } from '../../hooks/useRecordingNavigationGuard'
import { viewEnterSx } from '../../utils/animations'
import AppRoutes from '../../pages/AppRoutes'
import { Sidebar } from './Sidebar'
import { collapsedDrawerWidth, drawerWidth, headerHeight } from './shellSizes'
import { AppToolbar } from './Toolbar/AppToolbar'
import { BottomScrollFade } from '../common/BottomScrollFade'
import { SidebarWidthContext } from './SidebarWidthContext'
import { useSidebarOpen } from './useSidebarOpen'
import { LogoutConfirmDialog } from './LogoutConfirmDialog'
import { ConfirmDialog } from '../common/ConfirmDialog'
import GlobalSnackbar from './GlobalSnackbar'
import { PAGE_TOP_GAP } from '../common/PageContainer'

interface AuthenticatedLayoutProps {
    onLogout: () => Promise<void>
}

/** The shell shown only while signed in. Kept out of App so that every bit of session-scoped UI
 * state (sidebar, logout dialog, navigation guard) unmounts with the session — App itself stays
 * mounted across logout, its session gate being an early return. */
export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({ onLogout }) => {
    const { t } = useTranslation()
    const location = useLocation()
    const isMobile = useIsMobile()
    const { isOpen: sidebarOpen, toggle: handleSidebarToggle } = useSidebarOpen()
    // On phones the sidebar is a drawer over the page and takes no room beside it
    const desktopSidebarWidth = sidebarOpen ? drawerWidth : collapsedDrawerWidth
    const sidebarWidth = isMobile ? 0 : desktopSidebarWidth
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
    const [isLoggingOut, setIsLoggingOut] = useState(false)
    const navigationBlocker = useRecordingNavigationGuard()

    // On success, signOut flips the session tag and this layout unmounts into Login before
    // isLoggingOut's reset is ever seen; on failure, onLogout's own catch already showed the
    // error, so the dialog just stays open with its buttons re-enabled to retry.
    const handleConfirmLogout = async () => {
        setIsLoggingOut(true)
        await onLogout()
        setIsLoggingOut(false)
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <GlobalSnackbar />
            <Sidebar
                drawerWidth={drawerWidth}
                collapsedWidth={collapsedDrawerWidth}
                onLogoutClick={() => setIsLogoutDialogOpen(true)}
                open={sidebarOpen}
                onToggle={handleSidebarToggle}
                isMobile={isMobile}
            />
            <AppToolbar isMobile={isMobile} onMenuClick={handleSidebarToggle} />
            <LogoutConfirmDialog
                open={isLogoutDialogOpen}
                onClose={() => setIsLogoutDialogOpen(false)}
                onConfirm={handleConfirmLogout}
                pending={isLoggingOut}
            />
            <ConfirmDialog
                open={navigationBlocker.state === 'blocked'}
                title={t('encounter.leave_while_recording_title')}
                body={t('encounter.leave_while_recording')}
                cancelLabel={t('encounter.cancel')}
                confirmLabel={t('encounter.confirm')}
                // Re-checked here since TS can't narrow a closure read later by a click.
                onClose={() => {
                    if (navigationBlocker.state === 'blocked') navigationBlocker.reset()
                }}
                onConfirm={() => {
                    if (navigationBlocker.state === 'blocked') navigationBlocker.proceed()
                }}
            />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    width: '100%',
                    // Flex items default to min-width: auto and refuse to shrink below their
                    // content's natural width — without this, a wide page (e.g. Admin's table)
                    // pushed content off the viewport instead of scrolling when the sidebar opened.
                    minWidth: 0,
                    // Mobile's AppToolbar is fixed and needs clearing; desktop's logo lives in the
                    // sidebar, so pages start just below a gap the size of their side gutters.
                    mt: isMobile ? `${headerHeight}px` : 0,
                    pt: isMobile ? 0 : `${PAGE_TOP_GAP}px`,
                }}
            >
                <SidebarWidthContext.Provider value={sidebarWidth}>
                    {/* Keyed by pathname so every route change remounts and replays the entry animation */}
                    <Box key={location.pathname} sx={viewEnterSx}>
                        <AppRoutes />
                    </Box>
                </SidebarWidthContext.Provider>
                <BottomScrollFade />
            </Box>
        </Box>
    )
}
